import {
    useState,
    forwardRef,
    useImperativeHandle,
    useRef,
    useContext,
} from "react"

import {
    useMutation,
} from '@tanstack/react-query'

import { ExternalLink, Trash2, Upload, X, File } from "lucide-react"

import ApplicantDataFormContext from "../../Contexts/ApplicantDataFormContext"

import uploadFileMutation from "./services/upload-file"
import getMediaDocumentUrl from "./services/get-document-url"
import Overlay from "../Overlay"
import openUrlInNewTab from '../../helpers/open-url-in-new-tab'
import { isPasswordProtectedPdf, PASSWORD_PROTECTED_PDF_MESSAGE } from '../../helpers/password-protected-pdf'

const MediaInput = forwardRef(({ title, data, isMandatory, readOnly }, ref) => {

    const applicantDataForm = useContext(ApplicantDataFormContext)

    const requestKey = applicantDataForm.current?.["requestKey"]
    const saveProgress = applicantDataForm.current?.["saveProgress"]
    const backgroundRequestId = applicantDataForm.current?.["backgroundRequestId"]
    const formType = applicantDataForm.current?.["formType"]

    const uploadResponseRef = useRef()

    const [value, setValue] = useState("")
    const [error, setError] = useState(false)

    const fileInputRef = useRef()

    const [submittedKey, setSubmittedKey] = useState(data)

    const [keyReadyToSubmit, setKeyReadyToSubmit] = useState()

    const [isViewLoading, setIsViewLoading] = useState(false)

    // The uploaded media lives under S3 assets/private and can only be opened with a presigned
    // URL, fetched lazily when "View file" is clicked. Works in both contexts: authenticated
    // case-details (backgroundRequestId) and the public candidate form (requestKey).
    // The tab is opened inside the click (see openUrlInNewTab) so the pop-up blocker allows it.
    const handleView = (documentKey) => {
        if (!documentKey || isViewLoading) return
        setIsViewLoading(true)
        openUrlInNewTab(async () => (await getMediaDocumentUrl({ backgroundRequestId, requestKey, documentKey }))?.url)
            .catch(() => {
                // Best-effort — the document could not be resolved.
            })
            .finally(() => setIsViewLoading(false))
    }

    useImperativeHandle(ref, () => ({
        validateAndGetData: () => {

            if (keyReadyToSubmit) {
                setError("")
                uploadResponseRef.current = null
                return {
                    isValid: true,
                    value: keyReadyToSubmit,
                }

            }

            let validity = isMandatory ? !!submittedKey : true
            setError(validity ? "" : "Please upload a file")
            return {
                isValid: validity,
                value: submittedKey || "",
            }
        },
    }))

    const {
        error: mutationError,
        isPending: mutationIsPending,
        mutate: uploadFile,
        data: mutationData,
    } = useMutation({
        mutationFn: uploadFileMutation,
        onSuccess: async (uploadFileResponse) => {

            uploadResponseRef.current = uploadFileResponse

            if (saveProgress) {
                await saveProgress?.()
                setSubmittedKey(uploadFileResponse.key)
            }
            else {
                setKeyReadyToSubmit(uploadFileResponse.key)
            }


        }
    })

    const handleFile = async (file) => {
        if (!file) return
        if (await isPasswordProtectedPdf(file)) {
            setError(PASSWORD_PROTECTED_PDF_MESSAGE)
            if (fileInputRef.current) fileInputRef.current.value = ""
            return
        }
        const { name, extension } = splitFileName(file.name)
        setValue({ file, originalName: file.name, fileName: name, fileExtension: extension })
        setError("")
    }

    const handleDrop = (e) => {
        e.preventDefault()
        handleFile(e.dataTransfer.files[0])
    }

    const handleFileSelect = (e) => {
        e.preventDefault()
        handleFile(e.target.files[0])
    }

    const handleLocalDelete = () => {
        setValue()
        setSubmittedKey()
        setKeyReadyToSubmit()
        if (fileInputRef.current) {
            fileInputRef.current.value = ""; // 👈 clear the file input itself
        }
        let validity = isMandatory ? !!submittedKey : true
        setError(validity ? "" : "Please upload a file")
    }

    const handleUpload = () => {
        if (!value) {
            return
        }
        uploadFile({
            backgroundRequestId,
            key: requestKey,
            file: value.file,
            fileName: value.fileName,
            fileExtension: value.fileExtension,
            formType,
        })
    }

    return (
        <div className="my-2">
            {title && (
                <label className="block text-sm font-medium text-gray-700">{`${title}${isMandatory ? "*" : ""}`}</label>
            )}

            {
                submittedKey && (
                    <div className="flex items-center justify-between rounded-xl border p-4 bg-white shadow-sm">
                        <div>
                            <p className="text-sm text-gray-600">Data is already uploaded</p>
                            <button
                                type="button"
                                onClick={() => handleView(submittedKey)}
                                disabled={isViewLoading}
                                className="inline-flex items-center gap-1 text-blue-600 hover:text-blue-800 mt-1 text-sm font-medium disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                                <ExternalLink size={16} />
                                {isViewLoading ? "Opening…" : "View file"}
                            </button>
                        </div>

                        {
                            !readOnly && (
                                <button
                                    onClick={handleLocalDelete}
                                    className="inline-flex items-center gap-1 rounded-lg bg-red-600 text-white px-3 py-1.5 text-sm font-medium hover:bg-red-700 active:scale-95 transition"
                                >
                                    <Trash2 size={16} />
                                    Delete
                                </button>
                            )
                        }

                    </div>
                )
            }

            {/* File Upload */}
            {
                !submittedKey && !keyReadyToSubmit && (
                    <div className="space-y-3">
                        {/* Drop zone */}
                        <div
                            onClick={() => fileInputRef.current?.click()}
                            onDragOver={(e) => e.preventDefault()}
                            onDrop={handleDrop}
                            className={`border-2 border-dashed rounded-xl p-6 text-center cursor-pointer transition
          hover:bg-gray-50 ${error ? 'border-red-500' : 'border-gray-300'}`}
                        >
                            <p className="text-gray-600 font-medium">Drag and drop or click to select the file</p>
                            <p className="text-gray-400 text-sm mt-1">Supported file types: .pdf, .png, .jpg</p>
                            <input
                                ref={fileInputRef}
                                type="file"
                                accept="application/pdf,image/png,image/jpeg"
                                onChange={handleFileSelect}
                                className="hidden"
                            />
                        </div>

                        {/* Error message */}
                        {error && <p className="text-red-500 text-sm">{error}</p>}

                        {/* File selected row */}
                        {value && (
                            <div className="flex items-center justify-between p-3 rounded-lg bg-gray-50 border border-gray-200 shadow-sm">
                                <div className="flex items-center gap-2 min-w-0">
                                    <File className="text-gray-500 flex-shrink-0" size={20} />
                                    <span className="text-sm font-medium text-gray-700 truncate max-w-[200px]" title={value.originalName}>
                                        {value.originalName}
                                    </span>
                                </div>
                                <div className="flex items-center gap-2 flex-shrink-0">
                                    <button
                                        onClick={handleUpload}
                                        className="inline-flex items-center gap-1 rounded-lg bg-blue-600 text-white px-3 py-1.5 text-sm font-medium hover:bg-blue-700 active:scale-95 transition"
                                    >
                                        <Upload size={16} />
                                        Upload
                                    </button>
                                    <button
                                        onClick={handleLocalDelete}
                                        className="inline-flex items-center gap-1 rounded-lg bg-red-50 text-red-600 px-3 py-1.5 text-sm font-medium hover:bg-red-100 active:scale-95 transition"
                                    >
                                        <X size={16} />
                                        Delete
                                    </button>
                                </div>
                            </div>
                        )}
                    </div>
                )
            }

            {
                !submittedKey && keyReadyToSubmit && (
                    <div className="flex items-center justify-between rounded-xl border p-4 bg-white shadow-sm">
                        <div>
                            <p className="text-sm text-gray-600">Data is ready do submit</p>
                            <button
                                type="button"
                                onClick={() => handleView(keyReadyToSubmit)}
                                disabled={isViewLoading}
                                className="inline-flex items-center gap-1 text-blue-600 hover:text-blue-800 mt-1 text-sm font-medium disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                                <ExternalLink size={16} />
                                {isViewLoading ? "Opening…" : "View file"}
                            </button>
                        </div>

                        {
                            !readOnly && (
                                <button
                                    onClick={handleLocalDelete}
                                    className="inline-flex items-center gap-1 rounded-lg bg-red-600 text-white px-3 py-1.5 text-sm font-medium hover:bg-red-700 active:scale-95 transition"
                                >
                                    <Trash2 size={16} />
                                    Delete
                                </button>
                            )
                        }

                    </div>
                )
            }

            {mutationError && <p className="text-red-500 text-sm mt-1">Upload File Error</p>}

            {
                mutationIsPending && (
                    <Overlay />
                )
            }



        </div>
    )
})

export default MediaInput

const splitFileName = (filename) => {
    const lastDot = filename.lastIndexOf('.')
    if (lastDot === -1) return { name: filename, extension: '' }
    return { name: filename.slice(0, lastDot), extension: filename.slice(lastDot + 1) }
}
